import type { SqlRunner } from '../../../../lib/db.js';

/**
 * Who links — `prepareUserMetaForInsert(ctx.userMeta)` on the route. The
 * `created_*` half stamps new rows, the `updated_*` half rows that change.
 */
export interface LinkOrdersAuditMeta {
  createdAt?: Date | null;
  createdByImpersonateId?: string | null;
  createdByUserId?: string | null;
  createdByCompanyId?: string | null;
  createdByApiKeyId?: string | null;
  createdByApiKeyLabel?: string | null;
  updatedAt?: Date | null;
  updatedByImpersonateId?: string | null;
  updatedByImpersonateName?: string | null;
  updatedByUserId?: string | null;
  updatedByUserName?: string | null;
  updatedByCompanyId?: string | null;
  updatedByCompanyName?: string | null;
  updatedByApiKeyId?: string | null;
  updatedByApiKeyLabel?: string | null;
}

export interface LinkOrdersDbParams {
  purchaseOrder: {
    id: string;
    originTracingNodeId: string;
    destinationTracingNodeId: string;
  };
  salesOrder: { id: string; destinationTracingNodeId: string };
  /** The purchase order's current link — disconnected by the link. */
  oldTracingLink: { id: string; isInternal: boolean };
  /**
   * Set when a link from the purchase order's origin to the sales order's
   * destination already exists — a re-link, which only reconnects it. Absent
   * on a first link, which creates `newTracingLinkId` and carries the old
   * link's transactions and the old node's documents over to it.
   */
  existingTracingLinkId: string | null;
  newTracingLinkId: string;
  reflectionIds: { node: string; link: string };
  reflectInternalPurchaseOrderId: string | null;
  reflectInternalSalesOrderId: string | null;
  log: { id: string; payload: string; requestId: string | null };
  meta: LinkOrdersAuditMeta;
}

export const AUDIT_COLUMNS = [
  'created_at',
  'created_by_impersonate_id',
  'created_by_user_id',
  'created_by_company_id',
  'created_by_api_key_id',
  'created_by_api_key_label',
  'updated_at',
  'updated_by_impersonate_id',
  'updated_by_impersonate_name',
  'updated_by_user_id',
  'updated_by_user_name',
  'updated_by_company_id',
  'updated_by_company_name',
  'updated_by_api_key_id',
  'updated_by_api_key_label',
];

const UPDATED_COLUMNS = AUDIT_COLUMNS.filter(c => c.startsWith('updated_'));

export const auditValues = (meta: LinkOrdersAuditMeta): unknown[] => [
  meta.createdAt ?? null,
  meta.createdByImpersonateId ?? null,
  meta.createdByUserId ?? null,
  meta.createdByCompanyId ?? null,
  meta.createdByApiKeyId ?? null,
  meta.createdByApiKeyLabel ?? null,
  meta.updatedAt ?? null,
  meta.updatedByImpersonateId ?? null,
  meta.updatedByImpersonateName ?? null,
  meta.updatedByUserId ?? null,
  meta.updatedByUserName ?? null,
  meta.updatedByCompanyId ?? null,
  meta.updatedByCompanyName ?? null,
  meta.updatedByApiKeyId ?? null,
  meta.updatedByApiKeyLabel ?? null,
];

/** `$from, …` — one placeholder per audit column. */
export const auditPlaceholders = (from: number): string =>
  AUDIT_COLUMNS.map((_, i) => `$${from + i}`).join(', ');

const LOG_ACTION = 'LINK_ORDER';

/**
 * One `tracing_order_logs` entry — the write-only record the legacy order
 * actions keep of what they did, with the order pair and a JSON payload.
 */
export const insertOrderLogDb = async (
  db: SqlRunner,
  log: {
    id: string;
    purchaseOrderId: string | null;
    salesOrderId: string | null;
    action: string;
    payload: string;
    requestId: string | null;
    meta: LinkOrdersAuditMeta;
  },
): Promise<void> => {
  await db.runSql(
    `INSERT INTO tracing_order_logs (
       id, tracing_purchase_order_id, tracing_sales_order_id, action, payload, request_id,
       ${AUDIT_COLUMNS.join(', ')}
     )
     VALUES ($1, $2, $3, $4, $5, $6, ${auditPlaceholders(7)})`,
    [
      log.id,
      log.purchaseOrderId,
      log.salesOrderId,
      log.action,
      log.payload,
      log.requestId,
      ...auditValues(log.meta),
    ],
  );
};

/**
 * Links a purchase order to a sales order in the tracing graph. Run it inside
 * the caller's transaction — the writes belong together:
 *
 * 1. the `tracing_order_logs` entry for the link;
 * 2. the `tracing_order_links` pair (repeating a pair is a no-op);
 * 3. on a first link only: the old node's documents and the old link's
 *    transactions copied to the new node and link, and a `tracing_reflection`
 *    row for each (old → new);
 * 4. the new link, connected — created, or reconnected on a re-link;
 * 5. the old link disconnected.
 */
export const linkOrdersDb = async (
  db: SqlRunner,
  params: LinkOrdersDbParams,
): Promise<void> => {
  const { purchaseOrder, salesOrder, oldTracingLink, meta } = params;
  const audit = auditValues(meta);
  const oldNodeId = purchaseOrder.destinationTracingNodeId;
  const newNodeId = salesOrder.destinationTracingNodeId;

  await insertOrderLogDb(db, {
    ...params.log,
    purchaseOrderId: purchaseOrder.id,
    salesOrderId: salesOrder.id,
    action: LOG_ACTION,
    meta,
  });

  await db.runSql(
    `INSERT INTO tracing_order_links (
       tracing_sales_order_id, tracing_purchase_order_id,
       reflect_internal_purchase_order_id, reflect_internal_sales_order_id,
       ${AUDIT_COLUMNS.join(', ')}
     )
     VALUES ($1, $2, $3, $4, ${auditPlaceholders(5)})
     ON CONFLICT (tracing_sales_order_id, tracing_purchase_order_id) DO NOTHING`,
    [
      salesOrder.id,
      purchaseOrder.id,
      params.reflectInternalPurchaseOrderId,
      params.reflectInternalSalesOrderId,
      ...audit,
    ],
  );

  if (!params.existingTracingLinkId) {
    // The legacy copies had no ON CONFLICT, so a node already holding one of
    // the documents failed the whole link.
    await db.runSql(
      `INSERT INTO tracing_nodes_documents (tracing_node_id, tracing_document_id)
       SELECT $1::varchar, tracing_document_id
       FROM tracing_nodes_documents
       WHERE tracing_node_id = $2 AND tracing_node_id <> $1
       ON CONFLICT DO NOTHING`,
      [newNodeId, oldNodeId],
    );

    await db.runSql(
      `INSERT INTO tracing_links_transactions (tracing_link_id, tracing_transaction_id)
       SELECT $1::varchar, tracing_transaction_id
       FROM tracing_links_transactions
       WHERE tracing_link_id = $2 AND tracing_link_id <> $1
       ON CONFLICT DO NOTHING`,
      [params.newTracingLinkId, oldTracingLink.id],
    );

    for (const reflection of [
      {
        id: params.reflectionIds.node,
        assetId: oldNodeId,
        reflectionAssetId: newNodeId,
        assetType: 'TRACING_NODES',
      },
      {
        id: params.reflectionIds.link,
        assetId: oldTracingLink.id,
        reflectionAssetId: params.newTracingLinkId,
        assetType: 'TRACING_LINKS',
      },
    ]) {
      await db.runSql(
        `INSERT INTO tracing_reflection (
           id, asset_id, reflection_asset_id, asset_type, ${AUDIT_COLUMNS.join(', ')}
         )
         VALUES ($1, $2, $3, $4, ${auditPlaceholders(5)})`,
        [
          reflection.id,
          reflection.assetId,
          reflection.reflectionAssetId,
          reflection.assetType,
          ...audit,
        ],
      );
    }
  }

  // The new link is created by the company linking, whichever stamp the
  // caller's meta carries for it.
  const linkAudit = auditValues({
    ...meta,
    createdByCompanyId: meta.updatedByCompanyId ?? meta.createdByCompanyId,
  });
  await db.runSql(
    `INSERT INTO tracing_links (
       id, origin_tracing_node_id, destination_tracing_node_id, is_connected, is_internal,
       ${AUDIT_COLUMNS.join(', ')}
     )
     VALUES ($1, $2, $3, 1, $4, ${auditPlaceholders(5)})
     ON CONFLICT (id) DO UPDATE SET
       is_connected = EXCLUDED.is_connected,
       created_by_company_id = EXCLUDED.created_by_company_id,
       ${UPDATED_COLUMNS.map(c => `${c} = EXCLUDED.${c}`).join(',\n       ')}`,
    [
      params.newTracingLinkId,
      purchaseOrder.originTracingNodeId,
      newNodeId,
      oldTracingLink.isInternal ? 1 : 0,
      ...linkAudit,
    ],
  );

  // When the purchase order already ends at the sales order's node, the old and
  // new link are one row; the legacy UPDATE disconnected the link it had just
  // connected.
  if (oldTracingLink.id !== params.newTracingLinkId) {
    await db.runSql('UPDATE tracing_links SET is_connected = 0 WHERE id = $1', [
      oldTracingLink.id,
    ]);
  }
};

/**
 * Undoes a purchase-order ↔ sales-order link. Run it inside the caller's
 * transaction — the writes belong together:
 *
 * 1. the `tracing_order_logs` entry for the unlink;
 * 2. the `tracing_order_links` pair removed;
 * 3. the purchase order's own link reconnected and the link to the sales
 *    order's node disconnected — unless they are one row, which stays
 *    connected (the legacy unlink disconnected it).
 *
 * The documents, transactions and reflections the link copied stay, as in
 * legacy.
 */
export const unlinkOrdersDb = async (
  db: SqlRunner,
  params: {
    purchaseOrderId: string;
    salesOrderId: string;
    oldTracingLinkId: string;
    newTracingLinkId: string;
    log: { id: string; payload: string; requestId: string | null };
    meta: LinkOrdersAuditMeta;
  },
): Promise<void> => {
  await insertOrderLogDb(db, {
    ...params.log,
    purchaseOrderId: params.purchaseOrderId,
    salesOrderId: params.salesOrderId,
    action: 'UNLINK_ORDER',
    meta: params.meta,
  });

  await db.runSql(
    `DELETE FROM tracing_order_links
     WHERE tracing_sales_order_id = $1 AND tracing_purchase_order_id = $2`,
    [params.salesOrderId, params.purchaseOrderId],
  );

  const updated = auditValues(params.meta).slice(
    AUDIT_COLUMNS.length - UPDATED_COLUMNS.length,
  );
  const links: [string, number][] = [[params.oldTracingLinkId, 1]];
  if (params.newTracingLinkId !== params.oldTracingLinkId) {
    links.push([params.newTracingLinkId, 0]);
  }
  for (const [id, isConnected] of links) {
    await db.runSql(
      `UPDATE tracing_links
       SET is_connected = $1,
         ${UPDATED_COLUMNS.map((c, i) => `${c} = $${i + 2}`).join(', ')}
       WHERE id = $${UPDATED_COLUMNS.length + 2}`,
      [isConnected, ...updated, id],
    );
  }
};
